import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronRight, Menu, SlidersHorizontal, Tags } from 'lucide-react';
import { env } from '@config/env';
import { useHomeLayoutStore, useLicensedWidgets, resolveWidgets } from '@shared/stores/homeLayoutStore';
import { WIDGET_REGISTRY } from '@features/dashboard/widgets/registry';
import { tileColSpanClass } from '@shared/lib/tileGrid';
import { NotificationBell } from '@features/notifications/NotificationBell';
import { MoreCategoryTilesSheet } from '@features/dashboard/MoreCategoryTilesSheet';
import { PropertySwitcher } from '@features/dashboard/PropertySwitcher';
import { useModuleStore } from '@shared/stores/moduleStore';
import { useUiStore } from '@shared/stores/uiStore';
import { runTileAction } from '@shared/lib/tileActions';
import { homeTileGridClass } from '@shared/lib/tileGrid';
import { QuickTile } from '@features/dashboard/QuickTile';
import { useQuickLinks } from '@features/dashboard/useQuickLinks';
import { QuickLinkTile } from '@features/dashboard/QuickLinkTile';
import { TileIcon, TileLabel, homeTileButtonClass } from '@features/dashboard/TileIcon';
import { HeroHeader } from '@shared/ui/headers/HeroHeader';
import { BannerCarousel } from '@features/dashboard/BannerCarousel';
import { useHomeBannerLocations } from '@shared/hooks/useBannerLocation';
import type { CompiledMoreTileCategory } from '@shared/types/domainConfig';

/**
 * Home / dashboard — Fancourt layout: a domain-resolved photo header
 * carrying the greeting and property switcher, over a tile grid driven
 * by the tenant's domain config (`experienceStore.tiles.main`).
 */
export function Home(): JSX.Element {
  const navigate = useNavigate();
  const openNavDrawer = useUiStore((s) => s.openNavDrawer);
  const [categorySheet, setCategorySheet] = useState<CompiledMoreTileCategory | null>(null);
  const { topBanner, bottomBanner } = useHomeBannerLocations();

  // Widget mode: the member's Quick Links grid + configurable widgets below.
  const widgetMode = !!env.home?.widgetMode;
  const widgetOrder = useHomeLayoutStore((s) => s.order);
  const widgetEnabledOverride = useHomeLayoutStore((s) => s.enabledOverride);
  const licensedWidgets = useLicensedWidgets();
  const activeWidgets = useMemo(
    () =>
      resolveWidgets(licensedWidgets, {
        order: widgetOrder,
        enabledOverride: widgetEnabledOverride,
      }).filter((w) => w.enabled && WIDGET_REGISTRY[w.type]),
    [licensedWidgets, widgetOrder, widgetEnabledOverride],
  );
  const propertiesModuleEnabled = useModuleStore((s) => s.isModuleEnabled('properties'));

  const { homeLinks: shownQuickLinks, tiles, categoryTiles } = useQuickLinks(setCategorySheet);

  return (
    <div className="min-h-full bg-paper">
      {/*
        Sticky on the OUTER shell (not the header itself): a sticky child can't
        escape its parent's box, and this wrapper is exactly header-height, so
        sticking the wrapper is what actually pins the header while content
        scrolls under. The wrapper carries the rounded shadow; the header inside
        stays `relative` (sticky={false}) so its bg/overlay clip to the rounded
        corners. z-30 keeps it under modals/sheets/tab-bar (z-50+).
      */}
      <div className="sticky top-0 z-30 overflow-visible rounded-b-3xl shadow-[0_14px_32px_-8px_rgba(0,0,0,0.28)]">
        {/*
          Greeting header. Member name intentionally not shown in the nav bar —
          keep only the menu/property title (leading) and the notification bell (action).
        */}
        <HeroHeader
          sticky={false}
          showBack={false}
          // Greeting sits a little higher, with more room below it.
          className="pb-5"
          eyebrowClassName="mt-0"
          eyebrow={greetingFor(new Date())}
          title=""
          // No centred title here, so give the leading cluster every pixel the
          // bell doesn't need — the property title block reads, not truncates.
          topRowClassName="grid-cols-[minmax(0,1fr)_auto_auto]"
          leading={
            <div className="flex min-w-0 items-center gap-2">
              <button
                type="button"
                onClick={openNavDrawer}
                aria-label="Open menu"
                className="flex h-9 w-9 shrink-0 items-center justify-center rounded-pill bg-white/15 text-white backdrop-blur"
              >
                <Menu className="h-4.5 w-4.5" strokeWidth={1.75} />
              </button>
              {propertiesModuleEnabled ? <PropertySwitcher /> : null}
            </div>
          }
          // Bell only: an avatar beside it was too easy to tap by mistake. Profile is in the side menu.
          action={<NotificationBell variant="header" />}
        >
          {widgetMode ? (
            <p className="mt-2 text-[1.75rem] font-semibold leading-[2.125rem] tracking-[-0.01em] text-white">
              {greetingFor(new Date())}
            </p>
          ) : null}
        </HeroHeader>
      </div>

      {topBanner ? (
        <section className="px-5 pt-4">
          <BannerCarousel
            config={topBanner}
            className="aspect-[16/7] rounded-xl shadow-[0_12px_24px_-20px_rgba(17,24,39,0.45)]"
          />
        </section>
      ) : null}

      {/* ── Tiles ────────────────────────────────────────────────── */}
      {widgetMode ? (
        <>
        <section className="px-5 pt-6">
          {/* 1. Quick links — the member's picks, up to 8, in a 4-column grid. */}
          <div className="mb-3 flex items-center justify-between gap-3">
            <h2 className="text-[1.125rem] font-bold leading-tight text-ink">Quick links</h2>
            <button
              type="button"
              onClick={() => navigate('/app/home/customize')}
              className="flex shrink-0 items-center gap-0.5 py-1.5 text-body-sm font-semibold text-primary-700"
            >
              Customize
              <ChevronRight className="h-4 w-4" strokeWidth={2} />
            </button>
          </div>

          {shownQuickLinks.length > 0 ? (
            <ul className="grid grid-cols-4 gap-2.5">
              {shownQuickLinks.map((link) => (
                <li key={link.id}>
                  <QuickLinkTile link={link} onSelect={link.onSelect} />
                </li>
              ))}
            </ul>
          ) : null}
        </section>

        {/* 2. Widgets in the member's order, then the way into Customize home. */}
        <section className="flex flex-col gap-6 px-5 pt-6 pb-safe-or-6">
          {activeWidgets.map((widget) => {
            const def = WIDGET_REGISTRY[widget.type];
            if (!def) return null;
            const Widget = def.component;
            return <Widget key={widget.id} config={widget} />;
          })}

          <button
            type="button"
            onClick={() => navigate('/app/home/customize')}
            className="flex w-full items-center justify-center gap-1.5 rounded-xl bg-primary-50 py-3.5 text-body-sm font-semibold text-primary-800 transition-transform active:scale-[0.99]"
          >
            <SlidersHorizontal className="h-4 w-4" strokeWidth={2} />
            Customize home
          </button>
        </section>
        </>
      ) : (
        <section className="px-5 pt-6 pb-safe-or-6">
          {tiles.length > 0 || categoryTiles.length > 0 ? (
            <ul className={homeTileGridClass()}>
              {tiles.map((tile) => (
                <QuickTile key={tile.id} tile={tile} onSelect={() => runTileAction(tile, navigate)} />
              ))}
              {categoryTiles.map((category) => (
                <MoreCategoryTile
                  key={category.category_id}
                  label={category.name}
                  onSelect={() => setCategorySheet(category)}
                />
              ))}
            </ul>
          ) : (
            <p className="rounded-lg bg-sunken px-4 py-8 text-center text-body-sm text-ink-secondary">
              Your community hasn't published any quick links yet.
            </p>
          )}
        </section>
      )}

      {bottomBanner ? (
        <section className="px-5 pb-safe-or-6">
          <BannerCarousel
            config={bottomBanner}
            className="aspect-[16/7] rounded-xl shadow-[0_12px_24px_-20px_rgba(17,24,39,0.45)]"
          />
        </section>
      ) : null}

      <MoreCategoryTilesSheet
        category={categorySheet}
        open={categorySheet !== null}
        onOpenChange={(open) => {
          if (!open) setCategorySheet(null);
        }}
      />
    </div>
  );
}

function MoreCategoryTile({
  label,
  onSelect,
}: {
  label: string;
  onSelect: () => void;
}): JSX.Element {
  return (
    <li className={tileColSpanClass(1)}>
      <button type="button" onClick={onSelect} className={homeTileButtonClass}>
        <TileIcon label={label} fallbackIcon={Tags} />
        <TileLabel label={label} />
      </button>
    </li>
  );
}

function greetingFor(date: Date): string {
  const hour = date.getHours();
  if (hour < 12 && hour >= 5) return 'Good morning';
  if (hour < 17) return 'Good afternoon';
  return 'Good evening';
}
